/**
 * 26 - Courier Ambush (10 §2): no compound - a relay station's tents beside the road north of the belt. At
 * a stated time the courier sets out from the north edge with his escort and walks the road south; kill
 * him and take the satchel from his body. If the garrison is alarmed before he has set out he is warned
 * off and never comes; alarmed on the road he has ten seconds' warning and then runs for the north edge.
 *
 * ROUND4 §1: he RIDES. The courier is the man inside a staff car that drives the road while his escort walks
 * it; there is no `objectiveTarget` spawn of its own. Stop the car - a LAW, a charge, a mine, or anything solid
 * across the road - and he is in the wreck or out of it on foot, and only then is there a satchel to take. Let
 * it drive the length of its run and he is gone; alarm the garrison and it turns round and takes him back.
 *
 * His time is set by `finish` from the walk the finished map offers (helpers3bTime.ts).
 */

import type { EnemyKind } from '../../sim/types';
import { checker, guard, ruleOf, structure } from './helpers';
import { flagTargets, RECALL, wokenBy } from './helpers3b';
import { alongRoad, column, type ColumnMan, mainRoad, roadsideHide } from './helpers3bRoad';
import { carefulSeconds, fairTimer, timedRule } from './helpers3bTime';
import { roadRun, runPasses, vehicle, vehiclesOf } from './helpers4Vehicles';
import type { ArchetypeSpec } from './types';

const TAG = 'courier';
/** Seconds between the alarm and his turning back: long enough to be read, short enough to mean it. */
const WARNED_OFF = 10;

/** Two escorts by the design; a third with a strong garrison. */
export const escortsFor = (threat: number): number => (threat >= 7 ? 3 : 2);
/** The car keeps to a walking pace: its escort is on foot beside it, and the ambush must be able to catch it. */
const CAR_SPEED = 2;

export const courierAmbush: ArchetypeSpec = {
  id: 'courier-ambush',
  name: 'COURIER AMBUSH',
  nativeBiome: 'temperate',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'none',
  sealed: false,
  layout: 'compound',
  size: { w: [50, 58], d: [34, 38] },
  river: false,
  patrolLoop: 'outside',
  breach: { min: 0, max: 0 },

  build(g) {
    const road = mainRoad(g, true);
    // An ambush position is offered, not imposed: he may take the courier anywhere on the road.
    const hide = roadsideHide(g);
    const roadX = g.ctx.plan.roadEnd.x;
    const far = hide && hide.marker.x < roadX ? 1 : -1;
    // The relay station he sets out from: a tent or two by the road, a sentry at each.
    const camp = { x0: roadX + far * 4 - 9, z0: g.rect.z0 + 1, x1: roadX + far * 4 + 9, z1: g.rect.z0 + 12 };
    for (let i = 0; i < 2; i++) {
      const tent = structure(g, g.kit.shelter, { within: camp, margin: 1 });
      if (tent) guard(g, tent.x, tent.z + 3.6, g.rng.range(-0.4, 0.4));
    }

    const soldier: EnemyKind = g.elite || g.threat >= 6 ? 'elite' : 'rifleman';
    const men: ColumnMan[] = [];
    for (let i = 0; i < escortsFor(g.threat); i++) men.push({ kind: soldier });
    const south = (g.ctx.plan.lines[0]?.z ?? g.ctx.sizeZ - 80) - 8;
    column(g, alongRoad(road, 4 + men.length + 1, south), men, TAG);
    // R4: the courier himself, riding. The car sets out from the north end of the road with the escort.
    vehicle(g, {
      kind: 'staffCar', route: roadRun(road, 4, south), wakeTag: TAG, speed: CAR_SPEED,
      carries: 1, carriesTarget: true,
    });

    const markerId = hide?.marker.id ?? -1;
    const at = hide ? { x: hide.marker.x, z: hide.marker.z } : { x: roadX, z: g.rect.z1 };
    return {
      objective: {
        kind: 'lootTarget', label: 'TAKE THE SATCHEL', targetPropIds: [markerId], minFraction: 1,
        useLabel: 'TAKE THE SATCHEL', targetName: 'THE COURIER',
        rules: [
          {
            type: 'timedEvent', atSeconds: 300, effect: 'convoyCrosses', wakes: TAG,
            orders: 'THE COURIER COMES DOWN THE ROAD FROM THE NORTH AT THE STATED TIME: BE IN AMBUSH BY THEN',
            warning: 'THE COURIER IS DUE IN',
            reason: 'THE COURIER NEVER CAME',
          },
          {
            type: 'alertConsequence', level: 2, delaySeconds: WARNED_OFF, effect: 'targetFlees',
            orders: 'NO ALARM BEFORE HE COMES, OR HE IS WARNED OFF; ALARMED ON THE ROAD, HE RUNS BACK NORTH',
            warning: 'THE COURIER IS BEING WARNED OFF — TURNS BACK IN',
            reason: 'THE COURIER TURNED BACK',
          },
          RECALL,
        ],
      },
      finish(def) {
        flagTargets(def, [markerId]);
        const rule = timedRule(def, 'convoyCrosses');
        if (rule) rule.atSeconds = carefulSeconds(def, def.player, at);
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('courier-ambush');
    const o = def.objective;
    check(o.kind === 'lootTarget' && o.targetName === 'THE COURIER', 'objective must be lootTarget, and name the courier');
    // R4: he rides, so the man of the mission is the passenger of a car, not a spawn of his own.
    const cars = vehiclesOf(def.spawns);
    const [courier] = cars;
    check(def.spawns.every((s) => s.objectiveTarget !== true), 'the courier is aboard the car, not on the road on foot');
    check(cars.length === 1 && courier?.kind === 'staffCar', `needs exactly one staff car, has ${cars.length}`);
    check(courier?.carries === 1 && courier.carriesTarget === true, 'the car carries the courier, and nobody else');
    const comes = timedRule(def, 'convoyCrosses');
    const tag = comes?.wakes ?? 'convoyCrosses';
    check(
      courier?.dormantUntil === 'timedEvent' && courier.wakeTag === tag,
      'the courier must arrive with the timed event, and not before',
    );
    const escort = wokenBy(def, tag).filter((s) => s.route === undefined);
    check(escort.length === escortsFor(def.threat), `the courier needs ${escortsFor(def.threat)} escorts at threat ${def.threat}`);
    check(escort.every((s) => (s.patrol?.length ?? 0) >= 4), 'the escort must have a road to walk');
    check(courier?.route?.loop === false, 'a courier nobody stops drives off the end of his run, and is gone');
    check(courier !== undefined && courier.z < def.goalZone.z1, 'the courier must set out from the north end of the road');
    const flees = ruleOf(def, 'alertConsequence');
    check(flees?.effect === 'targetFlees' && flees.level === 2, 'alert 2 must turn the courier back');
    for (const rule of [comes, flees]) {
      check(
        rule?.orders !== undefined && rule.warning !== undefined && rule.reason !== undefined,
        'every rule must carry its orders, its countdown and its reason',
      );
    }
    const marker = def.props.find((p) => p.id === o.targetPropIds[0]);
    check(marker?.kind === 'overlookMarker' && marker.target === true, 'the ambush position must be marked on the strip map');
    if (marker) {
      check(probe.breach(marker.id) === 0, 'the ambush position must be reachable without crossing mines or wire');
      check(fairTimer(def, comes, def.player, marker), 'the courier must not come before a careful man can be in ambush');
      check(courier !== undefined && runPasses(courier, marker.x, marker.z, 26), 'the car must drive past the ambush');
    }
    return out;
  },
};
